<template>
  <div>
    <p class="text-muted">
      Notices are blocks of text the client must read and acknowledge, either with a tick box or with
      a typed name and drawn signature. The Client Declaration is a system notice shown on every form.
    </p>

    <b-row align-h="between" class="mb-1">
      <b-col cols="12" md="3">
        <b-form-input v-model="filter" placeholder="Search..." />
      </b-col>
      <b-col cols="12" md="3" class="d-flex justify-content-end">
        <b-button variant="success" @click="openCreate">
          New notice
        </b-button>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      responsive
      class="builder-table"
      :items="notices"
      :filter="filter"
      :filter-included-fields="['Title', 'Key']"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      show-empty
      empty-text="No notices defined."
      @filtered="onFiltered"
    >
      <template #cell(Title)="{ item }">
        <strong>{{ item.Title }}</strong>
        <small class="text-muted d-block"><code>{{ item.Key }}</code></small>
      </template>

      <template #cell(AckMode)="{ value }">
        <b-badge :variant="ackBadge(value)">
          <feather-icon :icon="value === 'Signature' ? 'PenToolIcon' : 'CheckSquareIcon'" size="12" class="mr-25" />
          {{ ackLabel(value) }}
        </b-badge>
      </template>

      <template #cell(DefaultSectionKey)="{ item }">
        <span v-if="item.DefaultSectionKey">
          <code>{{ item.DefaultSectionKey }}</code>
          <small class="text-muted">{{ item.SectionTitle || sectionTitle(data.sections, item.DefaultSectionKey) }}</small>
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(BodyLength)="{ item }">
        {{ item.BodyLength !== undefined ? item.BodyLength : (item.Body || '').length }} chars
      </template>

      <template #cell(UsageCount)="{ item }">
        <span v-if="item.IsSystem" class="text-muted">Every form</span>
        <span v-else-if="item.UsageCount">Used in {{ plural(item.UsageCount, 'item') }}</span>
        <span v-else class="text-muted">Not used</span>
      </template>

      <template #cell(IsSystem)="{ value }">
        <b-badge v-if="value" variant="light-secondary">System</b-badge>
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="flat-primary" class="btn-icon" title="Edit" @click="openEdit(item)">
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button
          size="sm"
          variant="flat-danger"
          class="btn-icon"
          title="Delete"
          :disabled="!!item.IsSystem"
          @click="confirmDelete(item)"
        >
          <feather-icon icon="TrashIcon" />
        </b-button>
      </template>
    </b-table>

    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />

    <notice-modal ref="noticeModal" :data="data" @saved="$emit('refresh')" />
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput,
} from 'bootstrap-vue'
import store from '@/store'
import NoticeModal from './NoticeModal.vue'
import {
  apiError, plural, ackBadge, ackLabel, sectionTitle,
} from './builderHelpers'

export default {
  name: 'NoticesTab',

  components: {
    BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, NoticeModal,
  },

  props: {
    data: { type: Object, required: true },
  },

  data() {
    return {
      filter: '',
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      fields: [
        { key: 'Title', label: 'Title', sortable: true },
        { key: 'AckMode', label: 'Acknowledgement', sortable: true },
        { key: 'DefaultSectionKey', label: 'Default section', sortable: true },
        { key: 'BodyLength', label: 'Body', sortable: true },
        { key: 'UsageCount', label: 'Usage', sortable: true },
        { key: 'IsSystem', label: '' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  computed: {
    notices() {
      return this.data.notices || []
    },
  },

  watch: {
    notices: {
      immediate: true,
      handler(list) { this.totalRows = list.length },
    },
  },

  methods: {
    plural,
    ackBadge,
    ackLabel,
    sectionTitle,

    onFiltered(items) {
      this.totalRows = items.length
      this.currentPage = 1
    },

    openCreate() {
      this.$refs.noticeModal.open(null)
    },

    openEdit(notice) {
      this.$refs.noticeModal.open(notice)
    },

    confirmDelete(notice) {
      const usage = notice.UsageCount
        ? ` It is used in ${plural(notice.UsageCount, 'package item')}; the server will refuse while it is in use.`
        : ''
      this.$bvModal.msgBoxConfirm(`Delete notice "${notice.Title}"?${usage}`, {
        title: 'Delete notice',
        okVariant: 'danger',
        okTitle: 'Delete',
        centered: true,
      }).then(ok => {
        if (!ok) return
        store.dispatch('settings/deleteOnboardingNotice', { id: notice.id })
          .then(res => {
            this.$bvToast.toast(res.data.message || 'Notice deleted.', { variant: 'success', title: 'Deleted', solid: true })
            this.$emit('refresh')
          })
          .catch(err => {
            this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
          })
      })
    },
  },
}
</script>
